import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X, User } from 'lucide-react'
import { usePartySearch } from '../../hooks/usePartySearch'

interface CounterpartyInputProps {
  values: string[]
  onChange: (values: string[]) => void
  actorId?: string
}

/**
 * Chips-style input for counterparty names.
 * Supports:
 * - Manual text entry (Enter or comma to add)
 * - Contact search autocomplete
 * - Click-to-remove chips
 */
export function CounterpartyInput({ values, onChange, actorId }: CounterpartyInputProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data: searchResults } = usePartySearch(debouncedQuery, actorId)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue.length >= 2 ? inputValue : '')
    }, 300)
    return () => clearTimeout(timer)
  }, [inputValue])

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDropdown])

  const addValue = useCallback((val: string) => {
    const trimmed = val.trim()
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed])
    }
    setInputValue('')
    setShowDropdown(false)
  }, [values, onChange])

  const removeValue = useCallback((index: number) => {
    onChange(values.filter((_, i) => i !== index))
  }, [values, onChange])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (inputValue.trim()) {
        addValue(inputValue)
      }
    } else if (e.key === 'Backspace' && !inputValue && values.length > 0) {
      removeValue(values.length - 1)
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <div
        className="flex flex-wrap gap-1 px-1.5 py-1 min-h-[30px] text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 focus-within:ring-1 focus-within:ring-primary-500/30 cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {values.map((val, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-[10px]"
          >
            {val}
            <button
              onClick={(e) => { e.stopPropagation(); removeValue(i) }}
              className="hover:text-primary-900 dark:hover:text-primary-100"
            >
              <X size={8} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            if (e.target.value.length >= 2) setShowDropdown(true)
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (inputValue.length >= 2) setShowDropdown(true) }}
          placeholder={values.length === 0 ? t('metadata.counterpartyPlaceholder') : ''}
          className="flex-1 min-w-[80px] bg-transparent text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none text-xs py-0.5"
        />
      </div>

      {/* Contact search dropdown */}
      {showDropdown && searchResults && searchResults.length > 0 && (
        <div className="absolute left-0 top-full mt-1 w-full bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50 py-1 max-h-[140px] overflow-y-auto">
          {searchResults.map((party) => (
            <button
              key={party.koblink_id}
              onClick={() => addValue(party.display_name)}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
            >
              <User size={10} className="shrink-0 text-neutral-400" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-neutral-900 dark:text-neutral-100 truncate">
                  {party.display_name}
                </p>
                {party.organization && (
                  <p className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                    {party.organization.display_name}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
